import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import GOOGLE_ICON from '@/ui/assets/images/google-icon-logo.svg';
import LINKEDIN_ICON from '@/ui/assets/icons/linkedinicon.png';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import { useAuth } from '@/lib/auth-provider';
import { storeAuthToken } from '@/lib/utils';
import { LANGUAGES } from '@/lib/constants';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import {
  ACCEPT_CIRCLE_INVITE,
  ACCEPT_INVITATION,
  ACCEPT_GLOBAL_LINK_INVITATION,
  SIGN_IN_WEB,
  SAVE_PERSONAL_DATA,
  SEND_SET_PASSWORD_EMAIL,
} from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';
import { LightShell, LightMark, LightSpinner, FitLine } from '../light-ui';
import { PhoneAuthFlow, type PhoneAuthSuccessPayload } from '@/components/PhoneAuthFlow';

interface SignInModalProps {
  open: boolean;
  onClose: () => void;
  invitationEmail?: string;
  invitationToken?: string;
  invitationType?: 'CIRCLE' | 'GUEST_CONTRIBUTOR' | 'INTERVIEW' | 'GLOBAL_LINK';
  onSignInSuccess: (result?: any, backgroundWork?: Promise<any>) => void;
  proxiCastName?: string;
  adminName?: string;
  /**
   * A texted Archive group interview's invitee with no login yet: the number
   * the invite went to, to sign up with once.
   */
  defaultPhone?: string;
  /** That text's link key, which lets the sign-up become the invitee's login. */
  inviteLinkKey?: string;
  /** An Archive interview: The Archivist greets them, not Proxi Interviewer. */
  isArchive?: boolean;
}

const SignInModal: React.FC<SignInModalProps> = ({
  open,
  onClose: _onClose,
  invitationEmail,
  invitationToken,
  invitationType,
  onSignInSuccess,
  proxiCastName,
  defaultPhone,
  inviteLinkKey,
  isArchive,
}) => {
  const { signInWithGoogle, signInWithLinkedIn, signInWithEmailPassword, signInWithPhone, loading } = useAuth();
  // Phone-OTP is the default auth method for both sign-in and sign-up — this
  // is the phone-first deep-link experience. Phone OTP unifies returning + new
  // users, so there's no separate signin/signup split for it; "Use email
  // instead" switches to the email form (which keeps the signin/signup mode
  // toggle). Name + channel + consent are captured later in
  // ContributorOnboarding, not in this modal.
  const [signupMethod, setSignupMethod] = useState<'phone' | 'email'>('phone');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingText, setLoadingText] = useState('Signing in…');
  const [selectedLanguage, setSelectedLanguage] = useState('ENG');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const [acceptCircleInvite] = useMutation(ACCEPT_CIRCLE_INVITE);
  const [acceptInvitation] = useMutation(ACCEPT_INVITATION);
  const [acceptGlobalLinkInvitation] = useMutation(ACCEPT_GLOBAL_LINK_INVITATION);
  const [signInWebMutation] = useMutation(SIGN_IN_WEB);
  const [sendSetPasswordEmail] = useMutation(SEND_SET_PASSWORD_EMAIL);

  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  // Email signup is gated on verification — onSignInSuccess only fires
  // after the user clicks the link in the verification email.
  const [pendingVerify, setPendingVerify] = useState<{
    email: string;
    backgroundWork: Promise<unknown>;
  } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);
  const verifyPollingRef = useRef<number | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (!pendingVerify) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const u = auth.currentUser;
        if (!u) return;
        await u.reload();
        if (cancelled) return;
        if (u.emailVerified) {
          if (verifyPollingRef.current) window.clearInterval(verifyPollingRef.current);
          await u.getIdToken(true);
          const work = pendingVerify.backgroundWork;
          setPendingVerify(null);
          onSignInSuccess(undefined, work);
        }
      } catch {
        // network blip — keep polling
      }
    };
    verifyPollingRef.current = window.setInterval(poll, 3000);
    void poll();
    return () => {
      cancelled = true;
      if (verifyPollingRef.current) window.clearInterval(verifyPollingRef.current);
    };
  }, [pendingVerify, onSignInSuccess]);

  const handleResendVerification = async () => {
    setResendError(null);
    const u = auth.currentUser;
    if (!u || !pendingVerify) return;
    try {
      await sendEmailVerification(u);
      setResendCooldown(30);
    } catch (err: any) {
      setResendError(err?.message || "Couldn't resend. Try again.");
    }
  };

  const handleCancelVerification = async () => {
    try {
      await auth.signOut();
    } catch {
      // ignore
    }
    setPendingVerify(null);
    setResendCooldown(0);
    setResendError(null);
  };

  useEffect(() => {
    if (invitationEmail) setEmail(invitationEmail);
  }, [invitationEmail]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (loading || isSubmitting) {
      setLoadingText(mode === 'signup' ? 'Creating account…' : 'Signing in…');
      timeout = setTimeout(() => setLoadingText('Preparing your interview…'), 2000);
    } else {
      setLoadingText(mode === 'signup' ? 'Creating account…' : 'Signing in…');
    }
    return () => clearTimeout(timeout);
  }, [loading, isSubmitting, mode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };
    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  if (!open) return null;

  const isGlobalLink = invitationType === 'GLOBAL_LINK';

  const getFriendlyAuthError = (err: any, fallback: string): string => {
    const code = err?.code;
    if (code === 'auth/popup-closed-by-user') return 'Sign-in was cancelled. Please try again.';
    if (code === 'auth/cancelled-popup-request') return 'Sign-in was cancelled. Please try again.';
    if (code === 'auth/wrong-password' || code === 'auth/user-not-found' || code === 'auth/invalid-credential') return 'Invalid email or password. Please try again.';
    if (code === 'auth/invalid-email') return 'Invalid email address.';
    if (code === 'auth/too-many-requests') return 'Too many attempts. Please try again later.';
    if (code === 'auth/network-request-failed') return 'Network error. Please check your connection and try again.';
    if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Please sign in instead.';
    if (code === 'auth/weak-password') return 'Password is too weak. Please use at least 6 characters.';
    return fallback;
  };

  const validateEmail = (userEmail: string) => {
    if (!invitationEmail) return true;
    return userEmail.toLowerCase().trim() === invitationEmail.toLowerCase().trim();
  };

  const acceptInvitationWithAuth = async (
    authToken: string,
    fName: string,
    lName: string,
  ) => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptCircleInvite({
      variables: {
        token: invitationToken,
        authToken,
        firstName: fName,
        lastName: lName,
      },
    });
    if (data?.acceptCircleInvite?.success && data.acceptCircleInvite.user) return data.acceptCircleInvite.user;
    throw new Error(data?.acceptCircleInvite?.message || 'Failed to accept invitation');
  };

  const acceptGlobalLinkInvitationWithAuth = async (signedInUser?: any) => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptGlobalLinkInvitation({
      variables: { input: { token: invitationToken } },
    });
    if (data?.acceptGlobalLinkInvitation?.success) {
      return {
        success: true,
        message: data.acceptGlobalLinkInvitation.message || 'Success',
        interview: data.acceptGlobalLinkInvitation.interview || null,
        category: data.acceptGlobalLinkInvitation.category || null,
        serviceData: signedInUser?.serviceData,
      };
    }
    throw new Error(data?.acceptGlobalLinkInvitation?.message || 'Failed to accept global link invitation');
  };

  // Unused but kept in case other entry points exist; satisfies the parity with
  // the original component's surface area.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _acceptInvitationForExistingUser = async () => {
    if (!invitationToken) throw new Error('No invitation token provided');
    const { data } = await acceptInvitation({ variables: { token: invitationToken } });
    if (data?.acceptInvitation?.success) {
      return { success: true, invitation: data.acceptInvitation.invitation, message: data.acceptInvitation.message };
    }
    throw new Error(data?.acceptInvitation?.message || 'Failed to accept invitation');
  };

  const handleSignIn = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await signInWithGoogle();
      if (user && user.personalData?.email) {
        if (validateEmail(user.personalData.email)) {
          if (isGlobalLink && invitationToken) {
            const result = await acceptGlobalLinkInvitationWithAuth(user);
            onSignInSuccess(result);
          } else {
            onSignInSuccess();
          }
        } else {
          setError(`Please sign in with the invited email: ${invitationEmail}`);
        }
      } else {
        if (isGlobalLink && invitationToken) {
          const result = await acceptGlobalLinkInvitationWithAuth(user);
          onSignInSuccess(result);
        } else {
          setError('Sign in succeeded but no user data was returned. Please try again.');
        }
      }
    } catch (err: any) {
      setError(getFriendlyAuthError(err, 'Sign in failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await signInWithLinkedIn();
      if (user && user.personalData?.email) {
        if (validateEmail(user.personalData.email)) {
          if (isGlobalLink && invitationToken) {
            const result = await acceptGlobalLinkInvitationWithAuth(user);
            onSignInSuccess(result);
          } else {
            onSignInSuccess();
          }
        } else {
          setError(`Please sign in with the invited email: ${invitationEmail}`);
        }
      } else {
        if (isGlobalLink && invitationToken) {
          const result = await acceptGlobalLinkInvitationWithAuth(user);
          onSignInSuccess(result);
        } else {
          setError('Sign in succeeded but no user data was returned. Please try again.');
        }
      }
    } catch (err: any) {
      setError(getFriendlyAuthError(err, 'LinkedIn sign in failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendSetPasswordEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email and we’ll send you a link.');
      return;
    }
    setResetSending(true);
    try {
      await sendSetPasswordEmail({ variables: { email: trimmed } });
      setResetSentTo(trimmed);
    } catch (err: any) {
      setError(err?.message || 'Could not send the email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  const handleEmailPasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!email || !password) {
        setError('Please enter both email and password');
        setIsSubmitting(false);
        return;
      }
      if (!validateEmail(email)) {
        setError(`Please use the invited email: ${invitationEmail}`);
        setIsSubmitting(false);
        return;
      }
      const user = await signInWithEmailPassword(email, password);
      if (user && user.personalData?.email) {
        if (validateEmail(user.personalData.email)) {
          if (isGlobalLink && invitationToken) {
            const result = await acceptGlobalLinkInvitationWithAuth(user);
            onSignInSuccess(result);
          } else {
            await onSignInSuccess();
          }
        } else {
          setError(`Please sign in with the invited email: ${invitationEmail}`);
        }
      } else {
        if (!invitationEmail && isGlobalLink && invitationToken) {
          const result = await acceptGlobalLinkInvitationWithAuth(user);
          onSignInSuccess(result);
        } else if (!invitationEmail) {
          await onSignInSuccess();
        } else {
          setError('Unable to retrieve user information. Please try again.');
        }
      }
    } catch (err: any) {
      setError(getFriendlyAuthError(err, 'Sign in failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailPasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!email || !password) {
        setError('Please enter email and password');
        setIsSubmitting(false);
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        setIsSubmitting(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const authToken = await userCredential.user.getIdToken();
      // Send verification email — onSignInSuccess only fires after the user
      // clicks the link (polled by useEffect above).
      sendEmailVerification(userCredential.user).catch(() => {});
      storeAuthToken(authToken);

      // Name + channel/phone/consent are collected later in
      // ContributorOnboarding via savePersonalData.
      const backgroundWork = (async () => {
        if (isGlobalLink) {
          await signInWebMutation({
            variables: {
              token: authToken,
              extraData: {
                email,
                userType: 'MEMBER',
                language: selectedLanguage,
              },
            },
            refetchQueries: [{ query: CURRENT_USER }],
            awaitRefetchQueries: true,
          });
        }
        if (isGlobalLink && invitationToken) {
          return acceptGlobalLinkInvitationWithAuth();
        }
        if (invitationToken) {
          return acceptInvitationWithAuth(authToken, '', '');
        }
        return undefined;
      })();

      setPendingVerify({ email, backgroundWork });
      setResendCooldown(30);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') setMode('signin');
      setError(getFriendlyAuthError(err, 'Failed to create account. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialSignUp = async (providerKind: 'google' | 'linkedin') => {
    setError(null);
    setIsSubmitting(true);
    try {
      const provider = providerKind === 'google'
        ? new GoogleAuthProvider()
        : new OAuthProvider('oidc.linkedin');
      const result = await signInWithPopup(auth, provider);
      const authToken = await result.user.getIdToken();
      storeAuthToken(authToken);

      // OAuth provides a displayName — split into first/last for the backend
      // user record. ContributorOnboarding still asks the user to confirm.
      const displayName = result.user.displayName || '';
      const nameParts = displayName.trim().split(/\s+/);
      const fName = nameParts[0] || '';
      const lName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      const backgroundWork = (async () => {
        if (isGlobalLink) {
          await signInWebMutation({
            variables: {
              token: authToken,
              extraData: {
                email: result.user.email,
                firstName: fName,
                lastName: lName,
                displayName: displayName || undefined,
                userType: 'MEMBER',
                language: selectedLanguage,
              },
            },
            refetchQueries: [{ query: CURRENT_USER }],
            awaitRefetchQueries: true,
          });
        }
        if (isGlobalLink && invitationToken) {
          return acceptGlobalLinkInvitationWithAuth();
        }
        if (invitationToken) {
          return acceptInvitationWithAuth(authToken, fName, lName);
        }
        return undefined;
      })();

      onSignInSuccess(undefined, backgroundWork);
    } catch (err: any) {
      setError(
        getFriendlyAuthError(
          err,
          providerKind === 'google'
            ? 'Failed to sign up with Google. Please try again.'
            : 'Failed to sign up with LinkedIn. Please try again.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    if (magicLinkMode) return handleSendSetPasswordEmail(e);
    return mode === 'signin' ? handleEmailPasswordSignIn(e) : handleEmailPasswordSignUp(e);
  };

  const handlePhoneAuthSuccess = async ({ firebaseToken, phoneE164 }: PhoneAuthSuccessPayload) => {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await signInWithPhone(firebaseToken, phoneE164, { invitationToken, inviteLinkKey });
      if (result.needsLinking) {
        setError(
          `An account already exists for ${result.maskedContact || 'this contact'}. Please sign up with email instead.`,
        );
        setSignupMethod('email');
        return;
      }
      if (result.error) {
        setError(result.error);
        return;
      }
      // Phone OTP succeeded — Proxi user exists. ContributorOnboarding will
      // collect name + consent next.
      if (isGlobalLink && invitationToken) {
        const linkResult = await acceptGlobalLinkInvitationWithAuth(result.user);
        onSignInSuccess(linkResult);
      } else if (invitationToken) {
        try {
          await acceptInvitationWithAuth(firebaseToken, '', '');
        } catch {
          // ignore — invitation may already be accepted
        }
        onSignInSuccess();
      } else {
        onSignInSuccess();
      }
    } catch (err: any) {
      setError(getFriendlyAuthError(err, 'Phone sign-in failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabled = isSubmitting || loading;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1150 }} role="dialog" aria-modal="true" aria-label={mode === 'signin' ? 'Sign In' : 'Sign Up'}>
      <LightShell scroll>
        {/* Language selector — top-right (signup only) */}
        {mode === 'signup' && (
          <div
            ref={languageDropdownRef}
            style={{
              position: 'absolute',
              top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
              right: 16,
              zIndex: 10,
            }}
          >
            <button
              type="button"
              onClick={() => setShowLanguageDropdown(v => !v)}
              disabled={disabled}
              className="lp-frost-chip"
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', padding: '6px 10px', opacity: disabled ? 0.6 : 1 }}
              aria-haspopup="listbox"
              aria-expanded={showLanguageDropdown}
            >
              <span>{selectedLanguage}</span>
              <ChevronDown style={{ width: 13, height: 13, transition: 'transform 180ms ease', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0)' }} />
            </button>
            {showLanguageDropdown && (
              <div
                role="listbox"
                className="lp-card lp-card-tight"
                style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 180, maxHeight: 240, overflowY: 'auto', padding: 4 }}
              >
                {LANGUAGES.map(lang => {
                  const selected = selectedLanguage === lang.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => { setSelectedLanguage(lang.code); setShowLanguageDropdown(false); }}
                      style={{
                        width: '100%', padding: '8px 10px', border: 0,
                        borderRadius: '2px 10px 2px 10px',
                        background: selected ? 'rgba(236,72,153,0.10)' : 'transparent',
                        color: selected ? 'var(--lp-ink)' : 'var(--lp-ink-soft)',
                        fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.06em',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span>{lang.label}</span>
                      <span style={{ fontSize: 10, color: 'var(--lp-ink-mute)' }}>{lang.code}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <main
          style={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'calc(env(safe-area-inset-top, 0px) + 24px) clamp(16px, 4vw, 32px) calc(env(safe-area-inset-bottom, 0px) + 80px)',
          }}
        >
          <div className="lp-card" style={{ width: '100%', maxWidth: 500, padding: 'clamp(24px, 4vw, 36px)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, marginBottom: 18 }}>
              <LightMark src={PROXI_BRAIN_LOGO} size={56} imgScale={0.72} />
              <h2 className="lp-section-heading" style={{ fontSize: 'clamp(22px, 3.4vw, 30px)' }}>
                {isArchive ? "Hi, I'm The Archivist." : "Hi, I'm Proxi Interviewer."}
              </h2>
              <p className="lp-prose" style={{ fontSize: 14, maxWidth: 380 }}>
                {isGlobalLink && proxiCastName ? (
                  <>Access your interviews or leave a story tip.</>
                ) : defaultPhone !== undefined ? (
                  'First time here? Confirm your number with a texted code. After this, your interview links open right away.'
                ) : invitationEmail ? (
                  <>This interview requires you to sign in with <span style={{ color: 'var(--lp-pink)' }}>{invitationEmail}</span>.</>
                ) : isArchive ? (
                  'Sign in to answer your interview.'
                ) : (
                  'Access your interviews or leave a story tip.'
                )}
              </p>
            </div>

            {error && <div className="lp-error" style={{ marginBottom: 14 }}>{error}</div>}

            {pendingVerify ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, padding: '12px 0' }}>
                <div style={{ width: 48, height: 48, borderRadius: 999, background: 'rgba(236,72,153,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--lp-pink)" strokeWidth="2"><path d="M4 4h16v16H4z" /><path d="m4 4 8 8 8-8" /></svg>
                </div>
                <p style={{ fontWeight: 600, fontSize: 16, color: 'var(--lp-ink)' }}>Check your inbox</p>
                <p className="lp-prose" style={{ fontSize: 13, maxWidth: 360 }}>
                  We sent a verification link to{' '}
                  <span style={{ color: 'var(--lp-pink)' }}>{pendingVerify.email}</span>. Click it to continue.
                </p>
                {resendError && <p style={{ color: '#dc2626', fontSize: 12 }}>{resendError}</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendCooldown > 0}
                    className={resendCooldown > 0 ? '' : 'lp-btn-link'}
                    style={{ fontSize: 12, opacity: resendCooldown > 0 ? 0.4 : 1, cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer' }}
                  >
                    {resendCooldown > 0 ? `resend in ${resendCooldown}s` : 'resend email'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelVerification}
                    className="lp-btn-link"
                    style={{ fontSize: 12 }}
                  >
                    wrong email?
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--lp-ink-mute)', fontSize: 12 }}>
                  <span className="lp-spinner-sm" />
                  waiting for verification…
                </div>
              </div>
            ) : (
            <>
            {signupMethod === 'phone' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <PhoneAuthFlow onSuccess={handlePhoneAuthSuccess} defaultPhone={defaultPhone} />
                {/* A texted invitee's first sign-up is by the number it went to:
                    only that becomes their login for the invite. Email,
                    Google and LinkedIn (behind this link) would make a
                    separate account the invite doesn't belong to. */}
                {defaultPhone === undefined && (
                  <button
                    type="button"
                    onClick={() => { setSignupMethod('email'); setError(null); }}
                    className="lp-btn-link"
                    style={{ alignSelf: 'center', fontSize: 12 }}
                  >
                    Use email instead
                  </button>
                )}
              </div>
            )}

            {signupMethod === 'email' && (
            <>
            <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                disabled={disabled}
                className="lp-input"
              />
              {!magicLinkMode && (
                <div className="lp-input-affix">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    disabled={disabled}
                    className="lp-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="lp-input-affix-end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={disabled || resetSending}
                className="lp-btn lp-btn-primary"
                style={{ marginTop: 4 }}
              >
                {(isSubmitting || resetSending) ? (
                  <>
                    <span className="lp-spinner-sm" />
                    <span>{resetSending ? 'Sending link…' : loadingText}</span>
                  </>
                ) : (
                  <span>{magicLinkMode ? 'Send link' : mode === 'signin' ? 'Sign in with email' : 'Sign up'}</span>
                )}
              </button>

              {mode === 'signin' && (
                <div style={{ textAlign: 'center', marginTop: 6 }}>
                  {resetSentTo ? (
                    <p className="lp-prose" style={{ fontSize: 13 }}>
                      Check <span style={{ color: 'var(--lp-ink)' }}>{resetSentTo}</span> for a link to set your password.
                    </p>
                  ) : magicLinkMode ? (
                    <button
                      type="button"
                      onClick={() => { setMagicLinkMode(false); setError(null); }}
                      disabled={resetSending}
                      className="lp-btn-link"
                    >
                      ← Back to sign in
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setMagicLinkMode(true); setError(null); }}
                      disabled={disabled}
                      className="lp-btn-link"
                    >
                      Forgot password, or don't have one yet?
                    </button>
                  )}
                </div>
              )}
            </form>

            {!magicLinkMode && (
              <>
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => { setSignupMethod('phone'); setError(null); }}
                    className="lp-btn-link"
                    style={{ fontSize: 12 }}
                  >
                    Use phone number instead
                  </button>
                </div>

                <div className="lp-divider"><span>or</span></div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    type="button"
                    onClick={mode === 'signin' ? handleSignIn : () => handleSocialSignUp('google')}
                    disabled={disabled}
                    className="lp-btn"
                  >
                    <img src={GOOGLE_ICON} width={20} height={20} alt="" />
                    <span>{loading ? loadingText : 'Continue with Google'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={mode === 'signin' ? handleLinkedInSignIn : () => handleSocialSignUp('linkedin')}
                    disabled={disabled}
                    className="lp-btn"
                  >
                    <img src={LINKEDIN_ICON} width={20} height={20} alt="" />
                    <span>{loading ? loadingText : 'Continue with LinkedIn'}</span>
                  </button>
                </div>

                <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', marginTop: 16 }}>
                  {mode === 'signin' ? (
                    <>
                      Don't have an account?{' '}
                      <button type="button" onClick={() => { setMode('signup'); setSignupMethod('phone'); setError(null); }} className="lp-btn-link">Sign up</button>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <button type="button" onClick={() => { setMode('signin'); setError(null); }} className="lp-btn-link">Sign in</button>
                    </>
                  )}
                </p>
              </>
            )}
            </>
            )}
            </>
            )}

            {/* FitLine keeps the whole sentence on one line at any viewport,
                shrinking it as needed instead of wrapping. */}
            <FitLine style={{
              marginTop: 18,
              fontFamily: 'var(--lp-mono)',
              fontSize: 10,
              letterSpacing: '0.04em',
              color: 'var(--lp-ink-mute)',
              lineHeight: 1.7,
            }}>
              By continuing, you accept our{' '}
              <a href="https://proxiaistudio.com/privacy" target="_blank" rel="noopener noreferrer" className="lp-link">Privacy Policy</a>{' '}
              and{' '}
              <a href="https://proxiaistudio.com/terms" target="_blank" rel="noopener noreferrer" className="lp-link">Terms of Use</a>.
            </FitLine>
          </div>
        </main>

        <footer
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
          role="contentinfo"
        >
          <span className="lp-footer-link">
            Powered by{' '}
            <a href="https://www.ProxiAIStudio.com" target="_blank" rel="noopener noreferrer">ProxiAIStudio.com</a>
          </span>
        </footer>
      </LightShell>
    </div>
  );
};

export default SignInModal;
